import { lazy } from 'react';

import {
  createBrowserRouter,
  Navigate,
  RouteObject,
  RouterProvider,
} from 'react-router-dom';

import { PrivateLayout, PublicLayout } from 'components';
import { routes } from 'enums';
import { SMS_DELIVERY_PAGE_ENABLED } from 'config/features';
import ResetPassword from 'pages/ResetPassword';
import ChangePassword from 'pages/ChangePassword';
import Users from 'pages/Users';
import Finances from 'pages/Finances';
import Dashboard from 'pages/Dashboard';
import Articles from 'pages/Articles';
import { Commission } from 'pages/Commission/Commission';

const Main = lazy(() => import('pages/Main'));
const Notification = lazy(() => import('pages/Notification'));
const User = lazy(() => import('pages/User'));
const Doctor = lazy(() => import('pages/Doctor'));
const Doctors = lazy(() => import('pages/Doctors'));
const Login = lazy(() => import('pages/Login'));
const Admins = lazy(() => import('pages/Admins'));
const Content = lazy(() => import('pages/Content'));
const Awareness = lazy(() => import('pages/Awareness'));
const OnboardingVideos = lazy(() => import('pages/OnboardingVideos'));
const SmsDelivery = lazy(() => import('pages/SmsDelivery'));
const SmsCodes = lazy(() => import('pages/SmsCodes'));
const AccountDeletions = lazy(() => import('pages/AccountDeletions'));

const publicRoutes: RouteObject[] = [
  { path: routes.AUTH.LOG_IN, element: <Login /> },
  { path: routes.AUTH.RESET_PASSWORD, element: <ResetPassword /> },
  { path: routes.AUTH.CHANGE_PASSWORD, element: <ChangePassword /> },
];

const privateRoutes: RouteObject[] = [
  { path: '*', element: <Navigate to={routes.MAIN.DASHBOARD} /> },

  { path: `${routes.MAIN.NOTIFICATION}`, element: <Notification /> },
  { path: `${routes.MAIN.USERS}`, element: <Users /> },
  { path: `${routes.MAIN.DOCTORS}`, element: <Doctors /> },
  { path: `${routes.MAIN.USER}/:id`, element: <User /> },
  { path: `${routes.MAIN.DOCTOR}/:id`, element: <Doctor /> },
  { path: `${routes.MAIN.FINANCES}`, element: <Finances /> },
  { path: `${routes.MAIN.ARTICLES}`, element: <Articles /> },
  { path: `${routes.MAIN.AWARENESS}`, element: <Awareness /> },
  { path: `${routes.MAIN.COMMISSION}`, element: <Commission /> },
  { path: `${routes.MAIN.DASHBOARD}`, element: <Dashboard /> },
  { path: `${routes.MAIN.ADMINS}`, element: <Admins /> },
  { path: `${routes.MAIN.CONTENT}`, element: <Content /> },
  {
    path: `${routes.MAIN.ONBOARDING_VIDEOS}`,
    element: <OnboardingVideos />,
  },
  // Not registered while hidden, so `/sms-delivery` falls to the catch-all
  // above and lands on the dashboard. The lazy import stays referenced, which
  // is what keeps the page compiling and type-checked while it is switched off.
  ...(SMS_DELIVERY_PAGE_ENABLED
    ? [{ path: `${routes.MAIN.SMS_DELIVERY}`, element: <SmsDelivery /> }]
    : []),
  { path: `${routes.MAIN.SMS_CODES}`, element: <SmsCodes /> },
  {
    path: `${routes.MAIN.ACCOUNT_DELETIONS}`,
    element: <AccountDeletions />,
  },
];

const router = createBrowserRouter([
  { element: <PublicLayout />, children: publicRoutes },
  { element: <PrivateLayout />, children: privateRoutes },
]);

export const Router = () => {
  return <RouterProvider router={router} />;
};
